import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { editCaseCreation } from "services/caseServices";
import { editReceivingInvestigationFile2 } from "services/prosecutorPoliceService";
import { CaseCreation, Roles } from "types";
import { errorHandlerOnForm, hasRole } from "utils/helper";

type Props = {
  caseData: CaseCreation;
  fetchAll: () => void;
};

const CourtReport: FC<Props> = ({ caseData, fetchAll }) => {
  const { t } = useTranslation();
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();
  const { state: receivingInvestigationFile } = useLocation();
  const [openForm, setOpenForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string>("");
  const [jurisdictionSource, setJurisdictionSource] = useState("");
  const [caseUpdatedByCourtDate, setCaseUpdatedByCourtDate] = useState<string>(
    new Date().toISOString().split("T")[0],
  );
  const [hasReport, setHasReport] = useState(false);
  const [currentReport, setCurrentReport] = useState<any>(null);

  // Check if there's already a court report from caseData using validation flags
  const getCaseSummary = useCallback(
    (status: string) => {
      switch (status) {
        case "pending":
          return t("court_report_case_needs_update");
        case "case_terminated_by_court":
          return t("court_report_case_terminated");
        case "out_of_jurisdiction":
          return t("court_report_out_of_jurisdiction");
        default:
          return t("court_report_status");
      }
    },
    [t],
  );

  const getCaseRemarks = useCallback(
    (status: string, source: string) => {
      switch (status) {
        case "pending":
          return t("court_report_case_needs_update_desc");
        case "case_terminated_by_court":
          return t("court_report_case_terminated_desc");
        case "out_of_jurisdiction":
          return source
            ? t("court_report_out_of_jurisdiction_referred", { source })
            : t("court_report_out_of_jurisdiction_desc");
        default:
          return "";
      }
    },
    [t],
  );

  const getFileSummary = useCallback(
    (status: string) => {
      switch (status) {
        case "case_terminated_by_court":
          return t("court_report_file_closed_terminated");
        case "out_of_jurisdiction":
          return t("court_report_file_closed_out_of_jurisdiction");
        default:
          return t("court_report_for_investigation_file");
      }
    },
    [t],
  );

  const getFileRemarks = useCallback(
    (status: string, source: string) => {
      switch (status) {
        case "case_terminated_by_court":
          return t("court_report_file_closed_terminated_desc");
        case "out_of_jurisdiction":
          return source
            ? t("court_report_file_closed_referred", { source })
            : t("court_report_file_closed_outside_jurisdiction");
        default:
          return "";
      }
    },
    [t],
  );

  useEffect(() => {
    const checkForExistingReport = () => {
      // Check case data for validation flags first
      if (caseData) {
        // Check for case updated by court
        if (caseData.is_case_updated_by_court) {
          setHasReport(true);
          const report = {
            status: "pending",
            date: caseData.case_updated_by_court_date,
            reason: caseData.case_updated_by_court_reason,
            summary: t("court_report_case_needs_update"),
            remarks: t("court_report_case_needs_update_desc"),
            flag: "is_case_updated_by_court",
          };
          setCurrentReport(report);
          return;
        }

        // Check for termination
        if (caseData.termination_status) {
          setHasReport(true);
          const report = {
            status: "case_terminated_by_court",
            date: caseData.case_updated_by_court_date,
            reason: caseData.case_updated_by_court_reason,
            summary: t("court_report_case_terminated"),
            remarks: t("court_report_case_terminated_desc"),
            flag: "termination_status",
          };
          setCurrentReport(report);
          return;
        }

        // Check for out of jurisdiction
        if (caseData.is_case_out_of_jurisdiction) {
          setHasReport(true);
          const report = {
            status: "out_of_jurisdiction",
            source: caseData.out_of_jursdicition_source,
            date: caseData.case_updated_by_court_date,
            reason: caseData.case_updated_by_court_reason,
            summary: t("court_report_out_of_jurisdiction"),
            remarks: caseData.out_of_jursdicition_source
              ? t("court_report_out_of_jurisdiction_referred", {
                  source: caseData.out_of_jursdicition_source,
                })
              : t("court_report_out_of_jurisdiction_desc"),
            flag: "is_case_out_of_jurisdiction",
          };
          setCurrentReport(report);
          return;
        }
      }

      // Fallback to status-based check
      const caseStatus = caseData?.status?.toString().toLowerCase();
      const fileStatus = receivingInvestigationFile?.status
        ?.toString()
        .toLowerCase();

      if (
        caseStatus?.includes("updated") ||
        caseStatus?.includes("terminated") ||
        caseStatus?.includes("jurisdiction") ||
        fileStatus?.includes("terminated") ||
        fileStatus?.includes("jurisdiction")
      ) {
        setHasReport(true);

        // Create report from whichever source has the status
        if (
          caseStatus &&
          (caseStatus.includes("updated") ||
            caseStatus.includes("terminated") ||
            caseStatus.includes("jurisdiction"))
        ) {
          const report = {
            status: caseData?.status,
            source: caseData?.out_of_jursdicition_source,
            // date: caseData?.updated_at,
            summary: getCaseSummary(caseData?.status || ""),
            remarks: getCaseRemarks(
              caseData?.status || "",
              caseData?.out_of_jursdicition_source || "",
            ),
          };
          setCurrentReport(report);
        } else if (
          fileStatus &&
          (fileStatus.includes("terminated") ||
            fileStatus.includes("jurisdiction"))
        ) {
          const report = {
            status: receivingInvestigationFile?.status,
            source: receivingInvestigationFile?.out_of_jursdicition_source,
            date: receivingInvestigationFile?.updated_at,
            summary: getFileSummary(receivingInvestigationFile?.status),
            remarks: getFileRemarks(
              receivingInvestigationFile?.status,
              receivingInvestigationFile?.out_of_jursdicition_source,
            ),
          };
          setCurrentReport(report);
        }
        return;
      }

      setHasReport(false);
      setCurrentReport(null);
    };

    checkForExistingReport();
  }, [
    caseData,
    receivingInvestigationFile,
    getCaseRemarks,
    getCaseSummary,
    getFileRemarks,
    getFileSummary,
    t,
  ]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedOption) {
      showTooltip(t("court_report_select_option_required"), "error");
      return;
    }

    if (
      selectedOption === "out_of_jurisdiction" &&
      !jurisdictionSource.trim()
    ) {
      showTooltip(t("court_report_jurisdiction_source_required"), "error");
      return;
    }

    if (!caseUpdatedByCourtDate) {
      showTooltip(t("court_report_date_required"), "error");
      return;
    }

    setLoading(true);

    try {
      // Update case status based on selection with validation flags
      switch (selectedOption) {
        case "case_on_updated":
          await editCaseCreation({
            id: caseData?.id,
            status: "Pending",
            old_status: caseData?.status,
            is_case_updated_by_court: true,
            case_updated_by_court_date: caseUpdatedByCourtDate,
            case_updated_by_court_reason: jurisdictionSource,
          });
          break;

        case "case_terminated_by_court":
          await editCaseCreation({
            id: caseData?.id,
            status: "case_terminated_by_court",
            old_status: caseData?.status,
            termination_status: true,
            case_updated_by_court_date: caseUpdatedByCourtDate,
            case_updated_by_court_reason: jurisdictionSource,
          });

          if (receivingInvestigationFile?.id) {
            await editReceivingInvestigationFile2({
              id: receivingInvestigationFile?.id,
              status: "case_terminated_by_court",
              reasone_for_update_case: jurisdictionSource,
            });
          }
          break;

        case "out_of_jurisdiction":
          await editCaseCreation({
            id: caseData?.id,
            status: "out_of_jurisdiction",
            old_status: caseData?.status,
            out_of_jursdicition_source: jurisdictionSource,
            is_case_out_of_jurisdiction: true,
            case_updated_by_court_date: caseUpdatedByCourtDate,
            case_updated_by_court_reason: jurisdictionSource,
          });

          if (receivingInvestigationFile?.id) {
            await editReceivingInvestigationFile2({
              id: receivingInvestigationFile?.id,
              status: "out_of_jurisdiction",
              out_of_jursdicition_source: jurisdictionSource,
            });
          }
          break;
      }

      showTooltip(t("court_report_submitted_successfully"), "success");
      setOpenForm(false);
      setSelectedOption("");
      setJurisdictionSource("");
      setCaseUpdatedByCourtDate(new Date().toISOString().split("T")[0]);
      fetchAll();
    } catch (error: any) {
      showTooltip(
        error?.response?.data?.detail || errorHandlerOnForm(error),
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const renderCurrentReport = () => {
    if (!currentReport) return null;

    let statusText = "";
    let statusColor = "";
    let statusDescription = "";

    const status = currentReport.status?.toString().toLowerCase();

    if (
      status?.includes("updated") ||
      currentReport.flag === "is_case_updated_by_court"
    ) {
      statusText = t("court_report_case_needs_update");
      statusColor = "bg-yellow-100 text-yellow-800 border-yellow-300";
      statusDescription = t("court_report_case_needs_update_short_desc");
    } else if (
      status?.includes("terminated") ||
      currentReport.flag === "termination_status"
    ) {
      statusText = t("court_report_case_terminated");
      statusColor = "bg-red-100 text-red-800 border-red-300";
      statusDescription = t("court_report_case_terminated_short_desc");
    } else if (
      status?.includes("jurisdiction") ||
      currentReport.flag === "is_case_out_of_jurisdiction"
    ) {
      statusText = t("court_report_out_of_jurisdiction");
      statusColor = "bg-blue-100 text-blue-800 border-blue-300";
      statusDescription = currentReport.source
        ? t("court_report_outside_jurisdiction_source", {
            source: currentReport.source,
          })
        : t("court_report_outside_jurisdiction");
    }

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-gray-900">
            {t("current_court_report")}
          </h4>
          <span
            className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border ${statusColor}`}
          >
            {statusText}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-2">{t("report_summary")}</p>
            <p className="text-gray-900 font-medium">
              {currentReport.summary || t("no_summary_provided")}
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-2">{t("court_date")}</p>
            <p className="text-gray-900 font-medium">
              {currentReport.date
                ? new Date(currentReport.date).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                : t("no_date_provided")}
            </p>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-2">
            {t("court_decision_or_reason")}
          </p>
          <p className="text-gray-900">
            {currentReport.reason ||
              currentReport.remarks ||
              t("no_details_provided")}
          </p>
        </div>

        {currentReport.source && (
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-2">
              {t("jurisdiction_source")}
            </p>
            <p className="text-gray-900">{currentReport.source}</p>
          </div>
        )}

        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-2">
            {t("status_description")}
          </p>
          <p className="text-gray-900">{statusDescription}</p>
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-sm text-gray-600 mb-2">{t("validation_flag")}</p>
          <p className="text-gray-900 font-mono text-sm">
            {currentReport.flag || t("status_based_report")}
          </p>
        </div>
      </div>
    );
  };

  const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);
  // Once the final decision file is on file, no more court reports are added.
  const hasFinalDecision = caseData?.documents?.some(
    (d) => d?.document_type === "final_court_decision",
  );

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-2 h-8 bg-red-600 rounded-full"></div>
            <h3 className="text-lg font-semibold text-gray-900">
              {t("court_report")}
            </h3>
          </div>

          {!hasReport && isProsecutor && !hasFinalDecision && (
            <button
              type="button"
              className="px-6 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
              onClick={() => setOpenForm(true)}
            >
              + {t("add_court_report")}
            </button>
          )}
        </div>

        {hasReport ? (
          renderCurrentReport()
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg
                className="w-16 h-16 mx-auto"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t("no_court_report_added")}
            </h3>
            <p className="text-gray-600 mb-6">
              {t("add_court_report_help_text")}
            </p>
          </div>
        )}
      </div>

      {/* Add Court Report Modal */}
      {openForm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={() => !loading && setOpenForm(false)}
            ></div>

            {/* Modal panel */}
            <div className="inline-block w-full max-w-lg p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t("add_court_report")}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {t("select_court_report_option_help")}
                  </p>
                </div>
                <button
                  onClick={() => !loading && setOpenForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="space-y-6">
                  {/* Court Date Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t("court_decision_date_required")}
                    </label>
                    <input
                      type="date"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      value={caseUpdatedByCourtDate}
                      onChange={(e) =>
                        setCaseUpdatedByCourtDate(e.target.value)
                      }
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {t("court_decision_date_help")}
                    </p>
                  </div>

                  {/* Court Report Options */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      {t("select_court_report_type_required")}
                    </label>
                    <div className="space-y-3">
                      <label className="flex items-start p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors has-[:checked]:border-red-500 has-[:checked]:bg-red-50">
                        <input
                          type="radio"
                          name="courtReportOption"
                          value="case_on_updated"
                          checked={selectedOption === "case_on_updated"}
                          onChange={(e) => {
                            setSelectedOption(e.target.value);
                            if (!jurisdictionSource) {
                              setJurisdictionSource(
                                "Case requires update based on court proceedings",
                              );
                            }
                          }}
                          className="mt-1 text-red-600 focus:ring-red-500"
                        />
                        <div className="ml-3">
                          <span className="font-medium text-gray-900">
                            {t("court_report_case_needs_update")}
                          </span>
                          <p className="text-sm text-gray-600 mt-1">
                            {t("court_report_case_needs_update_option_desc")}
                          </p>
                        </div>
                      </label>

                      <label className="flex items-start p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors has-[:checked]:border-red-500 has-[:checked]:bg-red-50">
                        <input
                          type="radio"
                          name="courtReportOption"
                          value="case_terminated_by_court"
                          checked={
                            selectedOption === "case_terminated_by_court"
                          }
                          onChange={(e) => {
                            setSelectedOption(e.target.value);
                            if (!jurisdictionSource) {
                              setJurisdictionSource(
                                "Case terminated by court decision",
                              );
                            }
                          }}
                          className="mt-1 text-red-600 focus:ring-red-500"
                        />
                        <div className="ml-3">
                          <span className="font-medium text-gray-900">
                            {t("court_report_case_terminated")}
                          </span>
                          <p className="text-sm text-gray-600 mt-1">
                            {t("court_report_case_terminated_option_desc")}
                          </p>
                        </div>
                      </label>

                      <label className="flex items-start p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors has-[:checked]:border-red-500 has-[:checked]:bg-red-50">
                        <input
                          type="radio"
                          name="courtReportOption"
                          value="out_of_jurisdiction"
                          checked={selectedOption === "out_of_jurisdiction"}
                          onChange={(e) => {
                            setSelectedOption(e.target.value);
                            setJurisdictionSource("");
                          }}
                          className="mt-1 text-red-600 focus:ring-red-500"
                        />
                        <div className="ml-3">
                          <span className="font-medium text-gray-900">
                            {t("court_report_out_of_jurisdiction")}
                          </span>
                          <p className="text-sm text-gray-600 mt-1">
                            {t("court_report_out_of_jurisdiction_option_desc")}
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Reason/Source Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {selectedOption === "out_of_jurisdiction"
                        ? t("jurisdiction_source_required")
                        : selectedOption === "case_terminated_by_court"
                          ? t("termination_reason_required")
                          : t("update_reason_required")}
                    </label>
                    <textarea
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      value={jurisdictionSource}
                      onChange={(e) => setJurisdictionSource(e.target.value)}
                      placeholder={
                        selectedOption === "out_of_jurisdiction"
                          ? t("jurisdiction_source_placeholder")
                          : selectedOption === "case_terminated_by_court"
                            ? t("termination_reason_placeholder")
                            : t("update_reason_placeholder")
                      }
                      rows={3}
                      required={selectedOption === "out_of_jurisdiction"}
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => {
                        setOpenForm(false);
                        setSelectedOption("");
                        setJurisdictionSource("");
                        setCaseUpdatedByCourtDate(
                          new Date().toISOString().split("T")[0],
                        );
                      }}
                      disabled={loading}
                      className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                    >
                      {t("cancel")}
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className={`flex-1 px-4 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors ${
                        loading ? "opacity-70 cursor-not-allowed" : ""
                      }`}
                    >
                      {loading ? (
                        <div className="flex items-center justify-center gap-2">
                          <svg
                            className="w-4 h-4 animate-spin"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          {t("submitting")}
                        </div>
                      ) : (
                        t("submit_report")
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourtReport;
